import React, { useState, useEffect } from 'react'
import {
	View,
	Text,
	TextInput,
	StyleSheet,
	TouchableOpacity,
	SafeAreaView,
	TouchableWithoutFeedback,
	Keyboard,
	Dimensions,
	Platform,
} from 'react-native'
import { FontAwesome } from '@expo/vector-icons'
import { parsePhoneNumberFromString, getCountries } from 'libphonenumber-js'
import { auth } from '../../../../firebase/config'
import { useAppDispatch, useAppSelector } from '../../../../state/hooks'
import { setCheckoutState, updateSelectedMethod } from '../../../../state/Slices/checkoutSlice'
import Colors from '../../../../constants/Colors'

import Formatter from '../../../../helpers/Formatter'
import { CHECKOUT_FORM_VALIDATION_ERRORS, FORM_INPUT } from '../../../../constants/texts/checkout'
import { DEFAULT_CHECKOUT_CONFIG } from '../../../../state/Slices/settingsSlice'
import PrivacyPolicyContent from '../../Components/PrivacyPolicyContent'
import { CheckboxInput } from '../../../../components/DesignSystem/CheckboxInput'
import { Stack, Text as DSText, Inline } from '../../../../components/DesignSystem'

const countryCodeToName: Record<string, string> = {
	US: 'The United States',
	CA: 'Canada',
}

// Can be moved to a helpers file.
const formValidators = {
	validateName: (name: string) => {
		return name.trim().length >= 2
	},
	validateEmail: (email: string) => {
		return /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(email)
	},
	validatePhoneNumber: phoneNumber => {
		try {
			const parsedNumber = parsePhoneNumberFromString(phoneNumber, 'US')
			return parsedNumber?.isValid() || false
		} catch (error) {
			return false
		}
	},
	validatePhoneNumberWithCountry: (phoneNumber, countryCode) => {
		try {
			const parsedNumber = parsePhoneNumberFromString(phoneNumber, countryCode)
			return parsedNumber?.isValid() && parsedNumber?.country === countryCode
		} catch (error) {
			return false
		}
	},
}

const getCountryName = countryCode => {
	const countryName = countryCodeToName[countryCode] || countryCode
	return countryName
}

const BasicInfo = ({ navigation, toggleConfirmModal, hasAttemptedNext, setAllowNext }) => {
	const dispatch = useAppDispatch()
	const { selectedMethods, savedPhone, takeOutName } = useAppSelector(state => state.checkout)
	const { userData } = useAppSelector(state => state.user)
	const { receiptPrinter, isDelivery } = useAppSelector(state => state.persisted)
	const {
		checkoutConfig,
		country,
		email: orgEmail,
		appDisplayOptions,
	} = useAppSelector(state => state.settings)

	const [keyboardActive, setKeyboardActive] = useState(false)
	const { displayName, email } = userData.user
	const [name, setName] = useState<string>(
		takeOutName !== '' ? takeOutName : displayName && !global.kiosk ? displayName : ''
	)
	const [isConsent, setIsConsent] = useState(false)
	const [errors, setErrors] = useState({
		name: '',
		phone: '',
		email: '',
		consent: '',
	})

	const [phoneNumberWarning, setPhoneNumberWarning] = useState(null)

	const isWeb = Platform.OS === 'web'
	const isKiosk = global.kiosk ?? false
	const isLoggedIn = auth.currentUser !== null && !global.kiosk

	const validatePhoneNumberCountry = isWeb && isDelivery

	// Enums
	const getConfigType = () => {
		if (isWeb) return auth.currentUser ? 'webMob' : 'guest'
		return 'kiosk'
	}

	const getOrgCheckoutConfiguration = () => {
		const type = getConfigType()
		const defaultsRequiredFields = DEFAULT_CHECKOUT_CONFIG[type].requiredFields
		const config = checkoutConfig[type].requiredFields
		// Merge defaults with config (config overrides defaults if there's overlap)
		const response = { ...defaultsRequiredFields, ...config }

		// If global.pos is true, make all fields optional
		if (global.pos) {
			return {
				name: true,
				phoneNumber: false,
				email: false,
			}
		}

		return response
	}

	const requiredFields = getOrgCheckoutConfiguration()

	const showPrivacyPolicy = !!orgEmail && appDisplayOptions.showTermsOfUse && !global.pos

	// Initialize form with user data if logged in
	useEffect(() => {
		if (isLoggedIn) {
			setName(takeOutName !== '' ? takeOutName : displayName)
			const savedPhoneNumber = savedPhone.slice(1) || userData.user.phone?.slice(1)
			const isDomesticPhone = formValidators.validatePhoneNumberWithCountry(
				savedPhoneNumber,
				country
			)

			if (!!savedPhoneNumber && validatePhoneNumberCountry) {
				if (!isDomesticPhone) {
					setPhoneNumberWarning(
						`We are unable to send delivery order updates to phone numbers outside of ${getCountryName(country)}`
					)
				}
			}

			dispatch(
				setCheckoutState({
					selectedMethods: {
						...selectedMethods,
						phoneString: savedPhone.slice(1) || userData.user.phone?.slice(1) || '',
						phoneStringFormatted: formatPhoneNumber(
							savedPhone.slice(1) || userData.user.phone?.slice(1) || ''
						),
						emailString: email || '',
						textMessage: validatePhoneNumberCountry ? isDomesticPhone : true,
						textUpdates: validatePhoneNumberCountry ? isDomesticPhone : true,
						email: true,
					},
				})
			)
		}
	}, [isLoggedIn])

	const formatPhoneNumber = inputString => {
		return Formatter.formatPhoneNumber(inputString)
	}

	const validateAllFields = () => {
		const newErrors: any = {}

		if (requiredFields.name && !formValidators.validateName(name || '')) {
			newErrors.name = CHECKOUT_FORM_VALIDATION_ERRORS.NAME_REQUIRED
		}

		if (
			((isWeb && isDelivery) || requiredFields.phoneNumber) &&
			!formValidators.validatePhoneNumber(selectedMethods.phoneString || '')
		) {
			newErrors.phone = CHECKOUT_FORM_VALIDATION_ERRORS.PHONE_NUMBER_REQUIRED
		}

		if (requiredFields.email && !formValidators.validateEmail(selectedMethods.emailString || '')) {
			newErrors.email = CHECKOUT_FORM_VALIDATION_ERRORS.EMAIL_ADDRESS_REQUIRED
		}

		if (!isConsent && showPrivacyPolicy) {
			newErrors.consent = CHECKOUT_FORM_VALIDATION_ERRORS.CONSENT_REQUIRED
		}

		setErrors(newErrors)
		return Object.keys(newErrors).length === 0
	}

	const checkForErrors = (obj, selectedMethods, name) => {
		// Map the form values to the required key names in the requiredFields property.
		const formValues = {
			name,
			phoneNumber: selectedMethods.phoneString,
			email: selectedMethods.emailString,
		}

		//Validate presence of each input if it's required. Default to true if not required.
		const fieldValidations = Object.entries(requiredFields).map(([key, required]) =>
			required ? formValues[key] !== '' : true
		)

		if (fieldValidations.includes(false)) return true

		if (Object.keys(obj).length === 0) return false

		const relevantErrors = { ...obj }

		return Object.keys(relevantErrors).length > 0
	}

	useEffect(() => {
		if (
			hasAttemptedNext ||
			((!requiredFields.name || name !== '') &&
				(!requiredFields.phoneNumber || selectedMethods.phoneString !== '') &&
				(!requiredFields.email || selectedMethods.emailString !== ''))
		) {
			validateAllFields()
		}
	}, [hasAttemptedNext, name, selectedMethods, isConsent])

	useEffect(() => {
		if (!checkForErrors(errors, selectedMethods, name)) {
			setAllowNext(true)
		} else {
			setAllowNext(false)
		}
	}, [errors])

	const handleNameChange = value => {
		setName(value)
		dispatch(
			setCheckoutState({
				takeOutName: value,
			})
		)

		if (hasAttemptedNext) {
			setErrors(prev => ({
				...prev,
				name: formValidators.validateName(value)
					? ''
					: CHECKOUT_FORM_VALIDATION_ERRORS.NAME_REQUIRED,
			}))
		}
	}

	const parseRawPhoneNumber = value => value.replace(/[^\d]/g, '')

	const handlePhoneChange = value => {
		const rawValue = parseRawPhoneNumber(value)
		const formattedValue = formatPhoneNumber(rawValue)
		dispatch(
			setCheckoutState({
				selectedMethods: {
					...selectedMethods,
					phoneString: rawValue,
					phoneStringFormatted: formattedValue,
				},
			})
		)

		const isPhoneValid = formValidators.validatePhoneNumber(value)
		const isDomesticPhone = formValidators.validatePhoneNumberWithCountry(value, country)

		if (value === '') {
			setPhoneNumberWarning(null)
		}

		if (validatePhoneNumberCountry) {
			if (isPhoneValid) {
				setPhoneNumberWarning(null)
			}

			if (!isDomesticPhone && isPhoneValid) {
				setPhoneNumberWarning(
					`We are unable to send delivery order updates to phone numbers outside of ${getCountryName(country)}`
				)

				if (selectedMethods.textMessage) {
					handleTextMessageToggle()
				}
			}
		}

		if (hasAttemptedNext) {
			setErrors(prev => ({
				...prev,
				phone: isPhoneValid ? '' : CHECKOUT_FORM_VALIDATION_ERRORS.PHONE_NUMBER_REQUIRED,
			}))
		}

		if (isPhoneValid && !selectedMethods.textMessage) {
			handleSMSUpdatesToggle(rawValue)
		} else if (!isPhoneValid && selectedMethods.textMessage) {
			handleTextMessageToggle()
			handleSMSUpdatesToggle(rawValue)
		}
	}

	const handleEmailChange = value => {
		dispatch(
			setCheckoutState({
				selectedMethods: { ...selectedMethods, emailString: value },
			})
		)

		const isEmailValid = formValidators.validateEmail(value)

		if (hasAttemptedNext) {
			setErrors(prev => ({
				...prev,
				email: isEmailValid ? '' : CHECKOUT_FORM_VALIDATION_ERRORS.EMAIL_ADDRESS_REQUIRED,
			}))
		}

		if (isEmailValid && !selectedMethods.email) {
			handleEmailToggle(value)
		} else if (!isEmailValid && selectedMethods.email) {
			handleEmailToggle(value)
		}
	}

	const handleTextMessageToggle = () => {
		const isPhoneValid = formValidators.validatePhoneNumber(selectedMethods.phoneString)
		const isDomesticPhone = formValidators.validatePhoneNumberWithCountry(
			selectedMethods.phoneString,
			country
		)

		// If toggling off, allow without validation
		if (selectedMethods.textMessage) {
			dispatch(updateSelectedMethod('textMessage', false))

			// If in kiosk mode, handle additional logic
			if (isKiosk) {
				dispatch(updateSelectedMethod('showNumPad', false))
			}
			return
		}

		// If toggling on, validate the phone number
		if (!isPhoneValid) {
			setErrors(prev => ({
				...prev,
				phone: CHECKOUT_FORM_VALIDATION_ERRORS.PHONE_NUMBER_REQUIRED_FOR_TEXT_MSG,
			}))
			return
		}

		if (!isDomesticPhone && validatePhoneNumberCountry) {
			return
		}

		// Clear the error and toggle on
		setErrors(prev => ({ ...prev, phone: undefined }))
		dispatch(updateSelectedMethod('textMessage', true))

		// If in POS mode, automatically check SMS updates
		if (global.pos) {
			dispatch(updateSelectedMethod('textUpdates', true))
		}

		// If in kiosk mode, handle additional logic
		if (isKiosk) {
			dispatch(updateSelectedMethod('showNumPad', true))
		}
	}

	const handleEmailToggle = (value = null) => {
		const isEmailValid = formValidators.validateEmail(
			value !== null && typeof value === 'string' ? value : selectedMethods.emailString
		)
		if (!isEmailValid && !selectedMethods.email) {
			setErrors(prev => ({
				...prev,
				email: CHECKOUT_FORM_VALIDATION_ERRORS.VALID_EMAIL_ADDRESS_REQUIRED,
			}))
			return
		}
		dispatch(updateSelectedMethod('email', !selectedMethods.email))
	}

	const handlePrintToggle = () => {
		dispatch(updateSelectedMethod('print', !selectedMethods.print))
	}

	const handleSMSUpdatesToggle = (rawValue = null) => {
		const isPhoneValid = formValidators.validatePhoneNumber(
			rawValue !== null && typeof rawValue === 'string' ? rawValue : selectedMethods.phoneString
		)
		const isDomesticPhone = formValidators.validatePhoneNumberWithCountry(
			rawValue !== null && typeof rawValue === 'string' ? rawValue : selectedMethods.phoneString,
			country
		)

		// If toggling off, allow without validation
		if (selectedMethods.textUpdates) {
			dispatch(updateSelectedMethod('textUpdates', false))
			return
		}

		// If toggling on, validate the phone number
		if (!isPhoneValid) {
			setErrors(prev => ({
				...prev,
				phone: CHECKOUT_FORM_VALIDATION_ERRORS.PHONE_NUMBER_REQUIRED_FOR_SMS_UPDATES,
			}))
			return
		}

		if (!isDomesticPhone && validatePhoneNumberCountry) {
			return
		}

		// Clear the error and toggle on
		setErrors(prev => ({ ...prev, phone: undefined }))
		dispatch(updateSelectedMethod('textUpdates', true))
	}

	const handleConsentToggle = () => {
		setIsConsent(!isConsent)
	}

	return (
		<TouchableWithoutFeedback onPress={isKiosk ? Keyboard.dismiss : null}>
			<SafeAreaView style={[styles.container, { zIndex: 1 }]}>
				<Stack gap="xxl">
					<Stack>
						<View
							style={{
								flexDirection: 'row',
								alignItems: 'center',
								justifyContent: 'space-between',
								width: '100%',
							}}
						>
							{!isLoggedIn && !global.kiosk && (
								<Text
									style={{
										color: Colors.greyscale[8],
										flex: 1,
										textAlign: 'right',
										marginBottom: 20,
										marginTop: 15,
									}}
								>
									{'Have an account? '}
									<TouchableOpacity
										onPress={() => {
											toggleConfirmModal()
											navigation.navigate('AccountStack', {
												screen: 'Account',
												params: { onCheckout: true, returnTo: 'TabCheckout' },
											})
										}}
									>
										<Text style={{ textDecorationLine: 'underline', fontWeight: 'bold' }}>
											{'Sign in'}
										</Text>
									</TouchableOpacity>
								</Text>
							)}
						</View>

						<View style={styles.inputWrapper}>
							<Stack gap="xs">
								<View style={[styles.inputContainer, errors.name ? styles.inputError : null]}>
									<View style={styles.iconContainer}>
										<FontAwesome
											name="user"
											size={20}
											color={errors.name ? '#dc3545' : Colors.greyscale[5]}
											style={styles.inputIcon}
										/>
									</View>
									<TextInput
										style={styles.input}
										placeholder={`Full name (${requiredFields.name ? FORM_INPUT.REQUIRED : FORM_INPUT.OPTIONAL})`}
										placeholderTextColor="#aaa"
										value={name}
										onChangeText={handleNameChange}
									/>
								</View>
								{errors.name ? (
									<DSText level="small" tone="critical">
										{errors.name}
									</DSText>
								) : null}
							</Stack>
						</View>

						<View style={styles.inputWrapper}>
							<Stack gap="xs">
								<View style={[styles.inputContainer, errors.phone ? styles.inputError : null]}>
									<View style={styles.iconContainer}>
										<FontAwesome
											name="mobile-phone"
											size={28}
											color={errors.phone ? '#dc3545' : Colors.greyscale[5]}
											style={styles.inputIcon}
										/>
									</View>
									<TextInput
										style={styles.input}
										placeholder={`Mobile number (${isWeb && isDelivery ? 'required' : requiredFields.phoneNumber ? FORM_INPUT.REQUIRED : FORM_INPUT.OPTIONAL})`}
										placeholderTextColor="#aaa"
										value={selectedMethods.phoneStringFormatted}
										maxLength={14}
										selectTextOnFocus={!isKiosk}
										onFocus={() => {
											if (isKiosk) {
												dispatch(updateSelectedMethod('showNumPad', true))
											} else {
												setKeyboardActive(true)
											}
										}}
										onBlur={() => {
											if (isKiosk) {
												dispatch(updateSelectedMethod('showNumPad', false))
											} else {
												setKeyboardActive(false)
											}
										}}
										onChangeText={handlePhoneChange}
										returnKeyType="done"
										keyboardType="numeric"
									/>
								</View>
								{phoneNumberWarning ? (
									<DSText level="small" tone="critical">
										{phoneNumberWarning}
									</DSText>
								) : null}
								{errors.phone ? (
									<DSText level="small" tone="critical">
										{errors.phone}
									</DSText>
								) : null}
							</Stack>
						</View>

						<View style={styles.inputWrapper}>
							<Stack gap="xs">
								<View style={[styles.inputContainer, errors.email ? styles.inputError : null]}>
									<View style={styles.iconContainer}>
										<FontAwesome
											name="envelope"
											size={18}
											color={errors.email ? '#dc3545' : Colors.greyscale[5]}
											style={styles.inputIcon}
										/>
									</View>
									<TextInput
										style={styles.input}
										placeholder={`Email address (${requiredFields.email ? FORM_INPUT.REQUIRED : FORM_INPUT.OPTIONAL})`}
										placeholderTextColor="#aaa"
										value={selectedMethods.emailString}
										onFocus={() => setKeyboardActive(true)}
										onBlur={() => setKeyboardActive(false)}
										onChangeText={handleEmailChange}
										returnKeyType="done"
										keyboardType="email-address"
									/>
								</View>
								{errors.email ? (
									<DSText level="small" tone="critical">
										{errors.email}
									</DSText>
								) : null}
							</Stack>
						</View>
					</Stack>

					<Stack>
						<DSText weight="semibold" tone="tertiary">
							How would you like your receipt?
						</DSText>
						<Inline alignX="left">
							{global.kiosk && receiptPrinter !== null && (
								<CheckboxInput
									label="Print"
									selected={selectedMethods.print}
									onPress={handlePrintToggle}
								/>
							)}
							<CheckboxInput
								label="Text Message"
								selected={selectedMethods.textMessage}
								onPress={handleTextMessageToggle}
							/>
							<CheckboxInput
								label="Email"
								selected={selectedMethods.email}
								onPress={handleEmailToggle}
							/>
						</Inline>
					</Stack>

					<Stack gap="xs">
						<Stack align="left">
							<DSText weight="semibold" tone="tertiary">
								Order update notifications
							</DSText>
							<CheckboxInput
								label="Receive updates via SMS"
								selected={selectedMethods.textUpdates}
								onPress={handleSMSUpdatesToggle}
							/>
						</Stack>
						{showPrivacyPolicy && (
							<CheckboxInput
								label="Receive updates via SMS"
								alignItems="flex-start"
								error={errors.consent}
								selected={isConsent}
								onPress={handleConsentToggle}
							>
								<PrivacyPolicyContent />
							</CheckboxInput>
						)}
					</Stack>
				</Stack>
			</SafeAreaView>
		</TouchableWithoutFeedback>
	)
}

const { width, height } = Dimensions.get('window')
const isLargeScreen = width > 1200 && height > width

const styles = StyleSheet.create({
	container: {
		flex: 1,
		backgroundColor: '#fff',
		padding: 20,
	},
	title: {
		fontSize: isLargeScreen ? 32 : 24, // Larger font for large screens
		fontWeight: 'bold',
		marginBottom: isLargeScreen ? 40 : 30,
	},
	formSection: {
		alignItems: 'flex-start',
		marginBottom: 25,
	},
	inputWrapper: {
		// marginBottom: 15,
		width: isLargeScreen ? '90%' : '100%', // Adjust width for large screens
	},
	inputContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		borderWidth: 1,
		borderColor: '#ddd',
		borderRadius: 8,
		paddingHorizontal: 12,
		paddingVertical: isLargeScreen ? 16 : 12, // Increase padding for large screens
	},
	inputError: {
		borderColor: '#dc3545',
	},
	iconContainer: {
		width: 40, // Adjust size to align with input height
		justifyContent: 'center',
	},
	inputIcon: {
		marginLeft: isLargeScreen ? '20%' : '10%', // Reduce padding for large screens
		marginRight: 10,
	},
	input: {
		flex: 1,
		padding: 12,
		fontSize: isLargeScreen ? 20 : 16, // Larger font for large screens
	},
	errorText: {
		color: '#dc3545',
		fontSize: isLargeScreen ? 14 : 12,
		marginTop: 5,
		marginLeft: 5,
	},
	checkboxGroup: {
		flexDirection: 'row',
		gap: isLargeScreen ? 30 : 20, // Increase gap for large screens
	},
	checkboxContainer: {
		flexDirection: 'row',
		alignItems: 'center',
		marginBottom: 10,
	},
	checkboxText: {
		fontSize: isLargeScreen ? 22 : 16,
		marginLeft: 10,
	},
	accountBox: {
		marginHorizontal: isLargeScreen ? 60 : 40,
		borderRadius: 8,
		padding: 20,
		justifyContent: 'center',
		alignItems: 'center',
		shadowColor: '#000',
		shadowOffset: { width: 0, height: 2 },
		shadowOpacity: 0.25,
		shadowRadius: 3.84,
		elevation: 16,
		borderWidth: 0.25,
		borderColor: Colors.greyscale[3],
	},
	signInButton: {
		backgroundColor: '#666',
		paddingVertical: isLargeScreen ? 16 : 12,
		paddingHorizontal: isLargeScreen ? 40 : 30,
		borderRadius: 25,
	},
	signInButtonText: {
		color: '#fff',
		fontSize: isLargeScreen ? 18 : 16,
		fontWeight: 'bold',
	},
})

export default BasicInfo
